import {
  Component,
  signal,
  ChangeDetectionStrategy,
  inject,
  computed,
  effect,
  untracked,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AuditLogService } from './shared/audit-log.service';
import {
  AuditLogFilters,
  AuditLogFilterValues,
} from './shared/audit-log-filters/audit-log-filters';
import {
  AuditLog as AuditLogEntry,
  AuditLogList,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { FrontendConfigurationService } from '@tourfold/shared-kernels/frontend-configuration';
import { finalize } from 'rxjs';
import { SortEvent } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  TfDataTableComponent,
  TfColumnDefDirective,
  ColumnDef,
  TfPageEvent,
  SettingsLoadingPlaceholder,
} from '@tourfold/libraries/shared-ui';
import {
  resolveSettingsLoadErrorDetails,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';
import {
  readDateParam,
  readStringParam,
  encodeDateParam,
  syncQueryParams,
} from '@tourfold/libraries/shared-util';
import { provideAuditLogAiPageContext } from './settings-audit-log.ai-page-context';

@Component({
  selector: 'tf-settings-page-audit-log',
  imports: [
    CommonModule,
    AuditLogFilters,
    TranslateModule,
    TooltipModule,
    ButtonModule,
    TfDataTableComponent,
    TfColumnDefDirective,
    SettingsLoadingPlaceholder,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './settings-audit-log.component.html',
  styleUrl: './settings-audit-log.component.scss',
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditLog {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly auditLogService = inject(AuditLogService);
  private readonly tenantConfigService = inject(FrontendConfigurationService);
  private readonly translateService = inject(TranslateService);
  private readonly tenantId = computed(() => this.tenantConfigService.tenantId());

  readonly columns: ColumnDef[] = [
    {
      field: 'originalEventTime',
      title: 'SETTINGS_AUDIT_LOG.FILTERS.EVENT_TIME',
      type: 'date',
      dateFormat: 'short',
      sortable: true,
    },
    {
      field: 'eventType',
      title: 'SETTINGS_AUDIT_LOG.FILTERS.EVENT_TYPE',
      type: 'custom',
      sortable: true,
    },
    {
      field: 'scopeType',
      title: 'SETTINGS_AUDIT_LOG.FILTERS.SCOPE_TYPE',
      type: 'text',
      sortable: true,
    },
    { field: 'scopeId', title: 'SETTINGS_AUDIT_LOG.FILTERS.SCOPE_ID', type: 'text' },
    {
      field: 'actorId',
      title: 'SETTINGS_AUDIT_LOG.FILTERS.ACTOR_ID',
      type: 'text',
      sortable: true,
    },
    { field: 'clientIp', title: 'SETTINGS_AUDIT_LOG.FILTERS.IP_ADDRESS', type: 'text' },
    { field: 'description', title: 'SETTINGS_AUDIT_LOG.FILTERS.DESCRIPTION', type: 'text' },
    { field: 'data', title: 'SETTINGS_AUDIT_LOG.FILTERS.DATA', type: 'custom' },
  ];

  dataEntries(value: unknown): Array<{ key: string; value: string }> {
    if (value == null || typeof value !== 'object') return [];
    return Object.entries(value as Record<string, unknown>).map(([key, raw]) => ({
      key,
      value: this.formatDataValue(raw),
    }));
  }

  private formatDataValue(raw: unknown): string {
    if (raw == null) return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
    return JSON.stringify(raw);
  }

  readonly auditLogs = signal<AuditLogEntry[]>([]);
  readonly isLoading = signal(false);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly totalRecords = signal(0);
  readonly sortField = signal<string | null>('original_event_time');
  readonly sortOrder = signal<number>(-1); // 1 for asc, -1 for desc, 0 for no sort
  readonly filterValues = signal<AuditLogFilterValues>({
    fromDate: readDateParam(this.route.snapshot.queryParams, 'fromDate'),
    toDate: readDateParam(this.route.snapshot.queryParams, 'toDate'),
    eventType: (readStringParam(this.route.snapshot.queryParams, 'eventType', '') !== ''
      ? readStringParam(this.route.snapshot.queryParams, 'eventType', '')
      : null) as AuditLogFilterValues['eventType'],
    scopeType: (readStringParam(this.route.snapshot.queryParams, 'scopeType', '') !== ''
      ? readStringParam(this.route.snapshot.queryParams, 'scopeType', '')
      : null) as AuditLogFilterValues['scopeType'],
    scopeId: readStringParam(this.route.snapshot.queryParams, 'scopeId', ''),
    actorId: readStringParam(this.route.snapshot.queryParams, 'actorId', ''),
  });

  private isInitializing = true;

  readonly hasActiveFilters = computed(() => {
    const filters = this.filterValues();
    return (
      filters.fromDate != null ||
      filters.toDate != null ||
      filters.eventType != null ||
      filters.scopeType != null ||
      filters.scopeId !== '' ||
      filters.actorId !== ''
    );
  });

  constructor() {
    effect(() => {
      const currentTenantId = this.tenantId();
      if (currentTenantId) {
        untracked(() => this.loadAuditLogs());
      }
    });
    provideAuditLogAiPageContext({
      auditLogs: this.auditLogs,
      filterValues: this.filterValues,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    });
  }

  private loadAuditLogs(page = 0, size = 50) {
    const currentTenantId = untracked(() => this.tenantId());
    const hadLoadedOnce = this.hasLoadedOnce();

    if (!currentTenantId) {
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    this.actionError.set(null);

    let fromDateStr: string | undefined;
    let toDateStr: string | undefined;

    const filters = untracked(() => this.filterValues());

    // Build sort array for backend in the format: ['field,asc'] or ['field,desc']
    const sortArray: string[] = [];
    const field = this.sortField();
    const order = this.sortOrder();
    if (field && order !== 0) {
      const direction = order === 1 ? 'asc' : 'desc';
      sortArray.push(`${field}:${direction}`);
    } else {
      // Default sort if no sort is set
      sortArray.push('original_event_time:desc');
    }

    if (filters.fromDate) {
      const year = filters.fromDate.getFullYear();
      const month = filters.fromDate.getMonth();
      const day = filters.fromDate.getDate();
      const fromDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      fromDateStr = fromDate.toISOString();
    }

    if (filters.toDate) {
      const year = filters.toDate.getFullYear();
      const month = filters.toDate.getMonth();
      const day = filters.toDate.getDate();
      const toDate = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
      toDateStr = toDate.toISOString();
    }

    this.auditLogService
      .getAuditLogsData({
        page: page,
        size: size,
        from: fromDateStr ? new Date(fromDateStr) : undefined,
        to: toDateStr ? new Date(toDateStr) : undefined,
        eventType: filters.eventType ?? undefined,
        scopeType: filters.scopeType ?? undefined,
        scopeId: filters.scopeId !== '' ? filters.scopeId : undefined,
        actorId: filters.actorId !== '' ? filters.actorId : undefined,
        sort: sortArray.length > 0 ? sortArray : undefined,
      })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: AuditLogList) => {
          this.auditLogs.set(response.items);
          this.totalRecords.set(response.page.totalElements);

          this.isLoading.set(false);
          this.loadError.set(null);
          this.loadErrorDetails.set(null);
          this.initialLoadFailed.set(false);
          this.hasLoadedOnce.set(true);
          this.markInitialized();
        },
        error: (error: unknown) => {
          this.isLoading.set(false);
          if (!hadLoadedOnce) {
            this.loadError.set(this.translateService.instant('SETTINGS.LOAD_ERROR.TITLE'));
            this.initialLoadFailed.set(true);
            void this.updateLoadErrorDetails(error);
          } else {
            this.actionError.set(this.translateService.instant('SETTINGS.LOAD_ERROR.MESSAGE'));
          }
          this.markInitialized();
        },
      });
  }

  onPageChange(event: TfPageEvent): void {
    const page = Math.floor(event.first / event.rows);
    this.loadAuditLogs(page, event.rows);
  }

  reloadAuditLogs(): void {
    // Reload audit logs from the first page with current filters and sort settings
    this.loadAuditLogs(0, 50);
  }

  retryLoad(): void {
    this.loadAuditLogs(0, 50);
  }

  clearActionError(): void {
    this.actionError.set(null);
  }

  onSort(event: SortEvent): void {
    // Ignore sort events during initialization
    if (this.isInitializing) {
      return;
    }

    // PrimeNG sort event: field is the column field, order is 1 (asc) or -1 (desc)
    // Check if sort actually changed to prevent infinite loops
    const newField = event.field ?? null;
    const newOrder = event.order ?? 0;

    if (this.sortField() === newField && this.sortOrder() === newOrder) {
      return; // Sort hasn't changed, don't reload
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);

    // Reload audit logs with new sort
    this.loadAuditLogs(0, 50);
  }

  onFilterApplied(filters: AuditLogFilterValues): void {
    this.filterValues.set(filters);
    this.syncFiltersToUrl(filters);
    this.loadAuditLogs(0, 50);
  }

  onFilterCleared(): void {
    const cleared: AuditLogFilterValues = {
      fromDate: null,
      toDate: null,
      eventType: null,
      scopeType: null,
      scopeId: '',
      actorId: '',
    };
    this.filterValues.set(cleared);
    this.syncFiltersToUrl(cleared);
    this.loadAuditLogs(0, 50);
  }

  private syncFiltersToUrl(filters: AuditLogFilterValues): void {
    syncQueryParams(this.router, this.route, {
      fromDate: encodeDateParam(filters.fromDate),
      toDate: encodeDateParam(filters.toDate),
      eventType: filters.eventType ?? undefined,
      scopeType: filters.scopeType ?? undefined,
      scopeId: filters.scopeId !== '' ? filters.scopeId : undefined,
      actorId: filters.actorId !== '' ? filters.actorId : undefined,
    });
  }

  private markInitialized(): void {
    if (this.isInitializing) {
      this.isInitializing = false;
    }
  }

  private async updateLoadErrorDetails(error: unknown): Promise<void> {
    this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
  }
}
